import { createApi } from '@reduxjs/toolkit/query/react';
import TicketDTO, { CreateTicketDTO, TicketReservation, UpdateTicketDTO } from '../../models/ticket';
import { Response } from '../../models/shared';
import { transformResponseData } from '../utils';
import baseQueryWithReauth from './base';
import { DisplaySeatDTO, ReservationDTO } from '../../models/seat';
// eslint-disable-next-line import/no-cycle
import { eventsApi } from './events';
import { usersApi } from './users';
import { setSelectedEvent } from '../slices/events';

interface GetReservationAndSeatsDTO {
  reservation: ReservationDTO;
  reservation_seats: DisplaySeatDTO[];
}

const ticketsApi = createApi({
  reducerPath: 'ticketsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['tickets'],
  endpoints: (build) => ({
    getTicketById: build.query<Response<TicketDTO>, { businessId: string, eventId: string, ticketId: string }>({
      query: ({ ticketId, businessId, eventId }) => ({
        url: `/businesses/${businessId}/events/${eventId}/tickets/${ticketId}`,
        method: 'GET',
      }),
    }),
    getAllTickets: build.query<(TicketDTO[] | null), { businessId: string, eventId: string, onlyActive?: boolean, publicSaleAvailable: boolean }>({
      providesTags: ['tickets'],
      query: ({
        businessId, eventId, onlyActive, publicSaleAvailable,
      }) => ({
        url: publicSaleAvailable ? `/businesses/${businessId}/events/${eventId}/tickets/public/all` : `/businesses/${businessId}/events/${eventId}/tickets`,
        method: 'GET',
        params: {
          ...(onlyActive != null && { active: onlyActive }),
          ...(publicSaleAvailable != null && { public_sale_available: publicSaleAvailable }),
        },
      }),
      transformResponse: (response: Response<TicketDTO[]>): (TicketDTO[] | null) => response.data || null,
    }),
    createTicket: build.mutation<Response<TicketDTO>, { ticket: CreateTicketDTO, businessId: string, eventId: string }>({
      query: ({
        ticket, businessId, eventId,
      }) => ({
        url: `/businesses/${businessId}/events/${eventId}/tickets`,
        method: 'POST',
        body: ticket,
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Invalidate tags to refetch queries used in other components
          dispatch(eventsApi.util.invalidateTags(['events']));
          dispatch(usersApi.util.invalidateTags(['user-events']));

          // Manually refetch the event and update the selectedEvent slice
          const eventQuery = dispatch(
            eventsApi.endpoints.getEventById.initiate({ eventId: arg.eventId }, { forceRefetch: true }),
          );
          const { data: updatedEvent } = await eventQuery;
          if (updatedEvent) {
            dispatch(setSelectedEvent(updatedEvent));
          }
          eventQuery.unsubscribe();
        } catch (e) {
          console.error(e);
        }
      },
      invalidatesTags: ['tickets'],
    }),
    updateTicket: build.mutation<Response<TicketDTO>, { ticket: UpdateTicketDTO, businessId: string, eventId: string }>({
      query: ({
        ticket, businessId, eventId,
      }) => ({
        url: `/businesses/${businessId}/events/${eventId}/tickets`,
        method: 'PUT',
        body: ticket,
      }),
      invalidatesTags: ['tickets'],
    }),
    deleteTicket: build.mutation<TicketDTO | null, { businessId: string, eventId: string, ticketId: string }>({
      query: ({
        ticketId, businessId, eventId,
      }) => ({
        url: `/businesses/${businessId}/events/${eventId}/tickets/${ticketId}`,
        method: 'DELETE',
      }),
      transformResponse: transformResponseData,
      invalidatesTags: ['tickets'],
    }),
    reserveTickets: build.mutation<Response<{ valid: boolean }>, { businessId: string, eventId: string, reservations: TicketReservation[] }>({
      query: ({
        businessId, eventId, reservations,
      }) => ({
        url: `/businesses/${businessId}/events/${eventId}/tickets/reservations`,
        method: 'POST',
        body: { reservations },
      }),
      invalidatesTags: ['tickets'],
    }),
    createReservation: build.mutation<Response<ReservationDTO>, { businessId: string, eventId: string }>({
      query: ({
        businessId, eventId,
      }) => ({
        url: `/businesses/${businessId}/events/${eventId}/reservations`,
        method: 'POST',
      }),
      invalidatesTags: ['tickets'],
    }),
    getReservationAndSeats: build.query<GetReservationAndSeatsDTO | null, { businessId: string, eventId: string, reservationCode: string }>({
      query: ({ businessId, eventId, reservationCode }) => ({
        url: `/businesses/${businessId}/events/${eventId}/seats/reservation/${reservationCode}`,
        method: 'GET',
      }),
      transformResponse: (response: Response<GetReservationAndSeatsDTO | null>) => response.data || null,
    }),
  }),
});

export const {
  useGetTicketByIdQuery, useGetAllTicketsQuery, useCreateTicketMutation, useUpdateTicketMutation, useDeleteTicketMutation, useReserveTicketsMutation, useCreateReservationMutation, useGetReservationAndSeatsQuery,
} = ticketsApi;
export { ticketsApi };
